import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert, UserPlus, Users } from 'lucide-react';
import {
  Button,
  Card,
  Chip,
  Dialog,
  EmptyState,
  Skeleton,
  toast,
} from '@/ui';
import { isOrgAdmin, orgTypeCode, type MemberType } from '@/auth/types';
import { useSessionStore } from '@/auth/session.store';
import { changeMemberType, fetchMembers, removeMember, type OrganizationMember } from './api';
import { InviteMemberDrawer } from './InviteMemberDrawer';
import { MemberTypeChip } from './MemberTypeChip';
import { organizationKeys } from './query-keys';
import { iamErrorMessage } from './error-message';
import { useZoneBase } from './zone-base';
import styles from './Organization.module.css';

/**
 * EKIP VE YETKILER (`/buro/ekip` ve `/musteri/ekip` — ayni bilesen iki bolgede).
 *
 * KENDI UYELIGI uzerinde islem YOK: sunucu CANNOT_MODIFY_OWN_MEMBERSHIP ile
 * reddediyor; butonu gostermek kullaniciyi calismayan bir kapiya surukler.
 * Karsilastirma STRING uzerinden — `appUserId` pg int8'tir ve sürücü onu
 * string dondurur; Number()'a cevirip === yapmak 2^53 ustunde sessizce
 * yanlis sonuc verir.
 *
 * YONETICI UYEYE "Yetkiler" baglantisi YOK: yonetici ortuk olarak tam
 * yetkilidir, sunucu onun icin grant satiri yazmaz.
 */

type PendingRemoval = { id: string; name: string };

export function OrganizationMembersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Ekran iki bolgede paylasiliyor (screens.ts): baglantilar bolgeye gore, yetki duzenleyici
  // ise YALNIZ buroda — musteri org'unda grant edilebilir bir yetki kumesi yok.
  const zoneBase = useZoneBase();
  const snapshot = useSessionStore((state) => state.snapshot);
  const admin = isOrgAdmin(snapshot);
  // Yetki grant'lari buro ici operasyon yetkileridir (teklif verme, islem tamamlama...).
  // Musteri org'unda personelin tek yetkisi sunucunun varsayilan verdigi viewAccounts;
  // duzenlenecek bir kume olmadigi icin ne "Yetkiler" baglantisi ne de sayac ciziliyor.
  const canEditPermissions = orgTypeCode(snapshot) === 'exchange';
  const selfUserId = snapshot?.user.id ?? '';

  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const members = useQuery({
    queryKey: organizationKeys.members(),
    queryFn: fetchMembers,
    enabled: admin,
  });

  /** Uye listesini ve ona bagli her seyi tazeler. */
  const invalidateMemberViews = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: organizationKeys.members() }),
      // memberCount ve rozet isletme kartinda duruyor.
      queryClient.invalidateQueries({ queryKey: organizationKeys.me() }),
      // Uye tipi degisimi grant setini de anlamsizlastirir (admin -> grant yok).
      queryClient.invalidateQueries({ queryKey: organizationKeys.memberPermissionsRoot() }),
      queryClient.invalidateQueries({ queryKey: organizationKeys.auditLogRoot() }),
    ]);
  };

  const changeType = useMutation({
    mutationFn: changeMemberType,
    onSuccess: async () => {
      toast({ message: t('members.roleUpdated'), tone: 'success' });
      await invalidateMemberViews();
    },
    onError: (error: unknown) => {
      toast({ message: iamErrorMessage(error, t), tone: 'danger' });
    },
    onSettled: () => {
      setBusyId(null);
    },
  });

  const remove = useMutation({
    mutationFn: removeMember,
    onSuccess: async () => {
      toast({ message: t('members.removed'), tone: 'success' });
      await invalidateMemberViews();
    },
    onError: (error: unknown) => {
      toast({ message: iamErrorMessage(error, t), tone: 'danger' });
    },
    onSettled: () => {
      setBusyId(null);
    },
  });

  if (!admin) {
    return (
      <Card padding="lg">
        <EmptyState
          tone="error"
          icon={<ShieldAlert size={28} aria-hidden="true" />}
          title={t('org.onlyAdmins')}
        />
      </Card>
    );
  }

  const list = members.data ?? [];

  const toggleType = (member: OrganizationMember): void => {
    const next: MemberType = member.memberType === 'admin' ? 'staff' : 'admin';
    setBusyId(member.id);
    changeType.mutate({ memberId: member.id, memberTypeCode: next });
  };

  return (
    <div className={styles.page}>
      <Card padding="md">
        <div className={styles.pageHead}>
          <div>
            <h1 className={styles.title}>{t('members.team')}</h1>
            {/* `total` YOK; gosterilen sayi YUKLENEN satirlarin sayisi. */}
            <p className={styles.subtitle}>
              {list.length} {t('org.profile.activeMembers')}
            </p>
          </div>
          <Button
            iconLeft={<UserPlus size={16} aria-hidden="true" />}
            onClick={() => {
              setInviteOpen(true);
            }}
          >
            {t('org.members.add')}
          </Button>
        </div>
      </Card>

      {members.isPending ? (
        <Card padding="lg">
          <Skeleton shape="block" height="220px" />
        </Card>
      ) : null}

      {members.isError ? (
        <Card padding="lg">
          <EmptyState
            tone="error"
            title={t('org.members.actionFailed')}
            description={iamErrorMessage(members.error, t)}
            action={
              <Button
                tone="neutral"
                onClick={() => {
                  void members.refetch();
                }}
              >
                {t('common.retry')}
              </Button>
            }
          />
        </Card>
      ) : null}

      {members.isSuccess && list.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={<Users size={28} aria-hidden="true" />}
            title={t('org.members.empty')}
          />
        </Card>
      ) : null}

      <div className={styles.memberList}>
        {list.map((member) => {
          // pg int8 STRING doner — String()/String() karsilastirmasi ZORUNLU.
          const isSelf = String(member.appUserId) === String(selfUserId);
          const isAdminMember = member.memberType === 'admin';
          const busy = busyId === member.id;

          return (
            <Card key={member.id} padding="md">
              <div className={styles.memberRow}>
                <div className={styles.memberMain}>
                  <span className={styles.memberName}>{member.userFullName}</span>
                  <span className={styles.muted}>{member.userEmail}</span>
                  <p className={styles.muted}>
                    {isAdminMember
                      ? t('members.allPermissions')
                      : canEditPermissions
                        ? `${member.permissionCount} ${t('members.permissionCount')}`
                        : t('members.staffMember')}
                  </p>
                </div>
                {/* Rozetler ve eylemler AYNI sag sutunda: eylemler rozetin altina
                    hizalanir, boylece kart yuksekligi satir basina bir blok kalir. */}
                <div className={styles.memberSide}>
                  <div className={styles.memberBadges}>
                    {isSelf ? <Chip tone="info">{t('members.you')}</Chip> : null}
                    <MemberTypeChip memberType={member.memberType} inactive={!member.isActive} />
                  </div>

                  {/* Kendi uyeligi: sunucu da reddediyor, eylem satiri hic cizilmez. */}
                  {!isSelf && member.isActive ? (
                    <div className={styles.actionRow}>
                      {!isAdminMember && canEditPermissions ? (
                        <Link
                          to={`${zoneBase}/ekip/${member.id}/yetkiler`}
                          className={styles.linkButton}
                        >
                          {t('org.members.permissions')}
                        </Link>
                      ) : null}
                      <Button
                        tone="neutral"
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          toggleType(member);
                        }}
                      >
                        {isAdminMember ? t('members.makeStaff') : t('members.makeAdmin')}
                      </Button>
                      <Button
                        tone="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          setPendingRemoval({ id: member.id, name: member.userFullName });
                        }}
                      >
                        {t('org.members.remove')}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <InviteMemberDrawer
        open={inviteOpen}
        onClose={() => {
          setInviteOpen(false);
        }}
      />

      <Dialog
        open={pendingRemoval !== null}
        tone="danger"
        title={t('members.removeTitle')}
        description={`${pendingRemoval?.name ?? ''} — ${t('members.removeDescription')}`}
        confirmLabel={t('org.members.remove')}
        cancelLabel={t('auth.give')}
        busy={remove.isPending}
        onCancel={() => {
          setPendingRemoval(null);
        }}
        onConfirm={() => {
          const target = pendingRemoval;
          setPendingRemoval(null);
          if (!target) return;
          setBusyId(target.id);
          remove.mutate({ memberId: target.id });
        }}
      />
    </div>
  );
}
